import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spin, Tooltip } from 'antd';
import { FileAddOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';

import SearchBox from 'components/molecules/SearchBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import Footer from 'components/molecules/PricingSimulator/Footer';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import TableErrorBox from 'components/molecules/ErrorBox/TableErrorBox';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import ReportPoller from 'components/molecules/TradePromoOptimization/BulkExport/ReportPoller';
import ReportSetupModal from 'components/molecules/TradePromoOptimization/Modals/ReportSetupModal';
import { tradePromoOptimizationUrls } from 'config/urls';
import { PENDING_BULK_EXPORT_STATUSES, TPO_UI_LIMITS, TpoMessages } from 'constants/tpo';
import { UserContext } from 'contexts/ConfigContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import type { ReportSetupConfig, ReportSetupSubmitPayload } from 'types/tpo/reportSetup';
import {
    BulkExportReportColumn,
    BulkExportReportRow,
    getBulkExportReportsTableColumns,
} from 'utils/getTradePromoOptimizationTableColumns';
import { formatNumberWithCommas, getConfig, sendNotification } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_REPORTS_BULK_EXPORT_CREATE_CLICKED, TPO_REPORTS_BULK_EXPORT_VIEWED } from 'utils/mixpanelEventNames';

const bulkExportTableId = 'bulk-export-reports-table-id';
const queryKeyPrefix = 'tpo-bulk-export-reports';

const TABLE_PAGE_SIZE = TPO_UI_LIMITS.BULK_EXPORT_REPORTS_PAGE_SIZE;
const MAX_CONCURRENT_POLLERS = TPO_UI_LIMITS.BULK_EXPORT_MAX_CONCURRENT_POLLERS;
const POLLER_CAP_MESSAGE = TpoMessages.error.BULK_EXPORT_POLLER_CAP(MAX_CONCURRENT_POLLERS);

const antIconDanger = <LoadingOutlined style={{ fontSize: 18, color: 'red' }} spin />;

type DeleteReportModalState = {
    open: boolean;
    data: BulkExportReportRow | null;
};
const initialDeleteReportModalState: DeleteReportModalState = { open: false, data: null };

type BulkExportTableVariables = {
    search_key?: string;
    offset: number;
    limit: number;
};

const BulkExportContainer: FC = () => {
    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const reportSetupConfig: ReportSetupConfig | undefined = tpoConfig?.bulkExportReportSetup;
    const tableColumnsConfig: BulkExportReportColumn[] = tpoConfig?.bulkExportTableColumns ?? [];

    const queryClient = useQueryClient();

    const [reportSetupModalOpen, setReportSetupModalOpen] = useState(false);
    const [viewModeReportId, setViewModeReportId] = useState<number | null>(null);
    const [editModeReportId, setEditModeReportId] = useState<number | null>(null);
    const [deleteReportModal, setDeleteReportModal] = useState<DeleteReportModalState>(initialDeleteReportModalState);
    const [refreshingIds, setRefreshingIds] = useState<Set<number | string>>(new Set());
    const [searchKey, setSearchKey] = useState('');
    const [tableVariables, setTableVariables] = useState<BulkExportTableVariables>({
        offset: 0,
        limit: TABLE_PAGE_SIZE,
    });

    const [bulkExportRows, setBulkExportRows] = useState<BulkExportReportRow[]>([]);
    const [bulkExportRowsCount, setBulkExportRowsCount] = useState(0);

    const tableHeight = useRef<number | null>(0);
    const tableScrollPositionRef = useRef<number>(0);

    tableHeight.current = useFillHeightToFitScreen({
        id: bulkExportTableId,
        height: tableHeight.current,
        buffer: 110,
    });

    useEffect(() => {
        trackEventMixpanel(TPO_REPORTS_BULK_EXPORT_VIEWED);
    }, []);

    const { loading: bulkExportRowsLoading, error: bulkExportRowsError } = useCommonApiHandler({
        method: 'get',
        url: tradePromoOptimizationUrls.getReportsListUrl,
        queryKey: [`${queryKeyPrefix}-list`, tableVariables],
        paramsVariables: tableVariables,
        parserFunction: (payload: any) => {
            const rows: BulkExportReportRow[] = (payload?.reports ?? []).map((r: any, idx: number) => ({
                ...r,
                key: r?.id ?? idx,
                created_at: r?.created_at ? dayjs.unix(r.created_at).format('MMM DD, YYYY') : null,
            }));
            const total = payload?.total_count ?? 0;
            if ((tableVariables.offset ?? 0) > 0) {
                setBulkExportRows((prev) => [...prev, ...rows]);
            } else {
                setBulkExportRows(rows);
                setBulkExportRowsCount(total);
            }
            return { data: rows, count: total };
        },
    });

    // merge a partial update into a single report row without re-fetching the list
    const patchRow = useCallback((reportId: number | string, rowPatch: Partial<BulkExportReportRow>) => {
        setBulkExportRows((prevRows) => prevRows.map((row) => (row.id === reportId ? { ...row, ...rowPatch } : row)));
    }, []);

    // rows currently consuming a poller slot (pending status + in-flight refresh POST)
    const inProgressRowIds = new Set<number | string>([
        ...bulkExportRows
            .filter((row) => row?.id != null && PENDING_BULK_EXPORT_STATUSES.has(String(row.status)))
            .map((row) => row.id as number | string),
        ...refreshingIds,
    ]);
    const atPollerCap = inProgressRowIds.size >= MAX_CONCURRENT_POLLERS;

    const onRefreshRow = (row: BulkExportReportRow) => {
        if (row?.id == null) return;
        if (PENDING_BULK_EXPORT_STATUSES.has(String(row.status))) return;
        const id: number | string = row.id;
        if (refreshingIds.has(id)) return;
        if (atPollerCap) {
            sendNotification('error', POLLER_CAP_MESSAGE);
            return;
        }

        setRefreshingIds((prev) => new Set(prev).add(id));
        sendNotification('info', 'Refreshing the report...');

        axios
            .post(tradePromoOptimizationUrls.getReportRefreshUrl(id))
            .then(() => {
                queryClient.invalidateQueries({ queryKey: [`${queryKeyPrefix}-list`] });
            })
            .catch((e: any) => {
                sendNotification(
                    'error',
                    e?.response?.data?.message ?? 'Failed to refresh the report. Please try again.',
                );
            })
            .finally(() => {
                setRefreshingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            });
    };

    const onEditRow = (row: BulkExportReportRow) => {
        if (row?.id == null) return;
        if (atPollerCap) {
            sendNotification('error', POLLER_CAP_MESSAGE);
            return;
        }
        setEditModeReportId(Number(row.id));
        setReportSetupModalOpen(true);
    };
    const onReportDetailsRow = (row: BulkExportReportRow) => {
        if (row?.id == null) return;
        setViewModeReportId(Number(row.id));
        setReportSetupModalOpen(true);
    };
    const onDeleteRow = (row: BulkExportReportRow) => {
        if (row?.id == null) return;
        setDeleteReportModal({ open: true, data: row });
    };

    const { loading: deleteReportLoading, mutate: deleteReportFn } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getReportDeleteUrl(deleteReportModal.data?.id ?? ''),
        method: 'delete',
    });

    const handleDeleteReport = () => {
        if (!deleteReportModal.data?.id) return;
        const { id, name } = deleteReportModal.data;
        deleteReportFn(
            {},
            {
                onSuccess: () => {
                    sendNotification('success', 'Report deleted successfully.');
                    setDeleteReportModal(initialDeleteReportModalState);
                    setBulkExportRows((prev) => prev.filter((r) => r.id !== id));
                    queryClient.invalidateQueries({ queryKey: [`${queryKeyPrefix}-list`] });
                },
                onError: (e: any) => {
                    sendNotification(
                        'error',
                        e?.response?.data?.message ?? `Failed to delete "${name}". Please try again.`,
                    );
                },
            },
        );
    };

    const columns = useMemo(
        () =>
            getBulkExportReportsTableColumns(
                tableColumnsConfig,
                {
                    onRefresh: onRefreshRow,
                    onEdit: onEditRow,
                    onReportDetails: onReportDetailsRow,
                    onDelete: onDeleteRow,
                    isRefreshing: (row) => row?.id != null && refreshingIds.has(row.id),
                },
                { sortBy: undefined, sortOrder: null },
            ),
        [tableColumnsConfig, atPollerCap, refreshingIds],
    );

    const onSearch = (val: string) => {
        tableScrollPositionRef.current = 0;
        setTableVariables((prev) => ({
            ...prev,
            search_key: val || undefined,
            offset: 0,
        }));
    };

    const onCreateNewReport = () => {
        if (atPollerCap) {
            sendNotification('error', POLLER_CAP_MESSAGE);
            return;
        }
        trackEventMixpanel(TPO_REPORTS_BULK_EXPORT_CREATE_CLICKED);
        setViewModeReportId(null);
        setEditModeReportId(null);
        setReportSetupModalOpen(true);
    };

    const closeReportSetupModal = () => {
        setReportSetupModalOpen(false);
        setViewModeReportId(null);
        setEditModeReportId(null);
    };

    const onReportSetupApply = (_payload: ReportSetupSubmitPayload) => {
        closeReportSetupModal();
        // Refetch the list so the newly-created report shows up.
        queryClient.invalidateQueries({ queryKey: [`${queryKeyPrefix}-list`] });
    };

    const onInfiniteScrollNext = () => {
        if (bulkExportRowsLoading) return;
        if (bulkExportRows.length >= bulkExportRowsCount) return;
        tableScrollPositionRef.current = 0;
        setTableVariables((prev) => ({
            ...prev,
            offset: (prev.offset ?? 0) + (prev.limit ?? TABLE_PAGE_SIZE),
        }));
    };

    const findScrollbarElement = () => {
        const container = document.querySelector(`#${bulkExportTableId}-container`);
        return container?.querySelector('.infinite-scroll-component') ?? null;
    };

    // Restore scroll position after appending a new page so the user stays put.
    useEffect(() => {
        if (bulkExportRows.length) {
            const scrollEl = findScrollbarElement();
            if (scrollEl && tableScrollPositionRef.current !== 0) {
                (scrollEl as HTMLElement).scrollTop = tableScrollPositionRef.current;
            }
        }
    }, [bulkExportRows]);

    const commonLoading = bulkExportRowsLoading;
    const footerFirstText = `Showing ${formatNumberWithCommas(bulkExportRows.length, 0)} of ${formatNumberWithCommas(
        bulkExportRowsCount,
        0,
    )} records`;

    const pendingRows = bulkExportRows
        .filter((row) => row.id != null && PENDING_BULK_EXPORT_STATUSES.has(String(row.status)))
        // hard-cap the number of mounted pollers
        .slice(0, MAX_CONCURRENT_POLLERS);
    const pendingPollers = pendingRows.map((row) => {
        const reportId = row.id as number | string;
        return (
            <ReportPoller key={reportId} reportId={reportId} initialStatus={row.status as string} onUpdate={patchRow} />
        );
    });

    return (
        <div id={`${bulkExportTableId}-container`} className="relative mt-12">
            {pendingPollers}
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-md m-0 font-semibold">Bulk Export Reports</h4>
                <div className="flex items-center gap-2">
                    <SearchBox search={searchKey} setSearch={setSearchKey} onSearch={onSearch} placeholder="Search" />
                    <Tooltip
                        title={atPollerCap ? POLLER_CAP_MESSAGE : ''}
                        placement="top"
                        destroyTooltipOnHide
                        overlayInnerStyle={{ fontSize: 11 }}
                    >
                        <Button
                            type="primary"
                            style={{ borderRadius: 4 }}
                            icon={<FileAddOutlined />}
                            onClick={onCreateNewReport}
                            disabled={atPollerCap}
                        >
                            Create New Report
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <InfiniteScroller
                data={bulkExportRows}
                loading={commonLoading}
                hasMore={bulkExportRows.length < bulkExportRowsCount || commonLoading}
                height={
                    bulkExportRows.length
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                }
                onNext={onInfiniteScrollNext}
            >
                <CustomTable
                    bordered
                    id={bulkExportTableId}
                    columns={columns}
                    dataSource={bulkExportRows}
                    pagination={false}
                    showSorterTooltip={false}
                    scroll={{ x: '100%' }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }}
                    style={{
                        opacity: commonLoading ? 0.3 : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: commonLoading ? (
                            <></>
                        ) : bulkExportRowsError ? (
                            <TableErrorBox error={bulkExportRowsError} height={tableHeight.current} />
                        ) : (
                            <div className="flex justify-center items-center py-6 text-fontGreyAlt font-xs">
                                No Reports Added.
                            </div>
                        ),
                    }}
                />
            </InfiniteScroller>
            <Footer type={bulkExportRowsError ? 'error' : 'default'} items={[footerFirstText, '', '', '']} />
            <ReportSetupModal
                open={reportSetupModalOpen}
                onCancel={closeReportSetupModal}
                onSubmit={onReportSetupApply}
                config={reportSetupConfig}
                mode={viewModeReportId != null ? 'view' : editModeReportId != null ? 'edit' : 'create'}
                reportId={viewModeReportId ?? editModeReportId}
            />
            <ConfirmationModal
                open={deleteReportModal.open}
                onCancel={() => setDeleteReportModal(initialDeleteReportModalState)}
                onSubmitClick={handleDeleteReport}
                loading={deleteReportLoading}
                okText={deleteReportLoading ? <Spin indicator={antIconDanger} /> : 'Delete'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                title="Delete Report"
                subTitle={`Are you sure you want to delete "${deleteReportModal.data?.name ?? ''}"?`}
                description="Reports once deleted cannot be recovered."
            />
        </div>
    );
};

export default BulkExportContainer;
